import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Heart,
  MessageCircle as CommentIcon,
  Bookmark,
  Send,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Repeat2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useSettings, buildWhatsAppLink, onWhatsAppClick } from "@/contexts/SettingsContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import VideoCard from "@/components/VideoCard";
import RepostMenu from "@/components/RepostMenu";
import Lightbox from "@/components/Lightbox";
import AudioPlayer from "@/components/AudioPlayer";
import { toast } from "sonner";

export default function ProjectPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [comments, setComments] = useState([]);
  const [engagement, setEngagement] = useState({ liked: false, saved: false });
  const [activeMedia, setActiveMedia] = useState(0);
  const [showRepost, setShowRepost] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [commentForm, setCommentForm] = useState({ name: "", message: "" });
  const [submittingComment, setSubmittingComment] = useState(false);
  const { settings } = useSettings();
  const viewedRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/projects/${slug}`);
        setProject(data);
        if (!viewedRef.current) {
          viewedRef.current = true;
          api.post(`/projects/${data.id}/view`).catch(() => {});
        }
        document.title = data.seo_title || `${data.title} | BuildDirect Africa`;
        const ensure = (selector, attr, content) => {
          let el = document.querySelector(selector);
          if (!el) {
            el = document.createElement("meta");
            const m = selector.match(/\[(.+?)="(.+?)"\]/);
            if (m) el.setAttribute(m[1], m[2]);
            document.head.appendChild(el);
          }
          el.setAttribute(attr, content);
        };
        const desc = data.seo_description || (data.description || "").slice(0, 155);
        const cover = data.cover_image || data.media?.[0]?.url || "";
        const fullCover = cover.startsWith("http") ? cover : `${process.env.REACT_APP_BACKEND_URL}${cover}`;
        ensure('meta[name="description"]', "content", desc);
        ensure('meta[property="og:title"]', "content", data.seo_title || data.title);
        ensure('meta[property="og:description"]', "content", desc);
        ensure('meta[property="og:image"]', "content", fullCover);
        ensure('meta[property="og:url"]', "content", window.location.href);
        ensure('meta[property="og:type"]', "content", "article");
        ensure('meta[name="twitter:card"]', "content", "summary_large_image");
        ensure('meta[name="twitter:title"]', "content", data.title);
        ensure('meta[name="twitter:description"]', "content", desc);
        ensure('meta[name="twitter:image"]', "content", fullCover);

        const [c, e] = await Promise.all([
          api.get(`/projects/${data.id}/comments`),
          api.get(`/projects/${data.id}/engagement`),
        ]);
        setComments(c.data);
        setEngagement(e.data);
      } catch (err) {
        console.error(err);
      }
    };
    load();
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!project) {
    return <div className="mx-auto max-w-[935px] px-5 py-20 text-[color:var(--bda-text-dim)]">Loading…</div>;
  }

  const mediaList = project.media?.length
    ? project.media
    : (project.cover_image ? [{ url: project.cover_image, type: "image" }] : []);
  const hasMany = mediaList.length > 1;

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/projects/${project.id}/like`);
      setEngagement((s) => ({ ...s, liked: data.liked }));
      setProject((p) => ({ ...p, like_count: data.like_count }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const toggleSave = async () => {
    try {
      const { data } = await api.post(`/projects/${project.id}/save`);
      setEngagement((s) => ({ ...s, saved: data.saved }));
      toast.success(data.saved ? "Saved" : "Unsaved");
    } catch {
      toast.error("Unable to save");
    }
  };

  const nativeShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: project.title, text: project.description?.slice(0, 120), url });
        api.post(`/projects/${project.id}/share?channel=native`).catch(() => {});
        return;
      } catch {/* dismissed */}
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
      api.post(`/projects/${project.id}/share?channel=copy_link`).catch(() => {});
    } catch {
      toast.error("Unable to share");
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.name || !commentForm.message) return;
    setSubmittingComment(true);
    try {
      await api.post(`/projects/${project.id}/comments`, commentForm);
      setCommentForm({ name: "", message: "" });
      toast.success("Comment submitted for review");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmittingComment(false);
    }
  };

  const activeItem = mediaList[activeMedia] || null;

  // Feature flags
  const fLike = settings?.show_likes !== false;
  const fComment = settings?.show_comments !== false;
  const fShare = settings?.show_share !== false;
  const fRepost = settings?.show_repost !== false;
  const fSave = settings?.show_save !== false;
  const fWhatsapp = settings?.show_whatsapp_button !== false;

  return (
    <div className="mx-auto max-w-[935px]" data-testid="project-page">
      <div className="px-5 md:px-10 py-3 md:py-5 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[color:var(--bda-text-muted)] hover:text-[color:var(--bda-text)]" data-testid="post-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>

      <article className="bg-[color:var(--bda-surface)] border-y md:border border-[color:var(--bda-border)] md:mx-5 md:mx-10 md:mb-10 overflow-hidden">
        {/* Header */}
        <header className="flex items-center gap-3 px-4 md:px-5 py-3.5" data-testid="post-header">
          <Link to="/" className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-[color:var(--bda-surface-2)] flex items-center justify-center p-1">
            <img src={settings?.logo_url || "/builddirect-logo.png"} alt="" className="w-full h-full object-contain" />
          </Link>
          <div className="flex-1 min-w-0">
            <Link to="/" className="text-sm font-semibold text-[color:var(--bda-text)] hover:underline line-clamp-1">
              {settings?.brand_name || "builddirectafrica"}
            </Link>
            {project.location && (
              <div className="text-xs text-[color:var(--bda-text-dim)] flex items-center gap-1 line-clamp-1">
                <MapPin className="w-3 h-3" /> {project.location}
              </div>
            )}
          </div>
        </header>

        {/* Media */}
        <div className="relative bg-black" data-testid="post-media">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeMedia}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="relative"
            >
              {activeItem?.embed_provider === "youtube" ? (
                <div className="aspect-square md:aspect-[4/5] bg-black relative" data-testid="post-media-youtube">
                  {/* Show the YouTube thumbnail underneath the iframe so the
                      visitor sees the cover image instantly instead of a
                      black square for 4-5s while the iframe + player JS
                      load. The iframe paints on top and hides the image
                      once the video is ready to display. */}
                  {activeItem.poster_url && (
                    <img
                      src={activeItem.poster_url}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  <iframe
                    src={`https://www.youtube.com/embed/${activeItem.embed_id}?rel=0&modestbranding=1&playsinline=1&autoplay=1&mute=1`}
                    title={project.title}
                    loading="eager"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              ) : activeItem?.embed_provider === "vimeo" ? (
                <div className="aspect-square md:aspect-[4/5] bg-black relative" data-testid="post-media-vimeo">
                  {activeItem.poster_url && (
                    <img
                      src={activeItem.poster_url}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  <iframe
                    src={`https://player.vimeo.com/video/${activeItem.embed_id}?autoplay=1&muted=1&playsinline=1&title=0&byline=0&portrait=0`}
                    title={project.title}
                    loading="eager"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              ) : (activeItem?.type === "video" || /\.(mp4|webm|mov|m4v|ogg|ogv)$/i.test(activeItem?.url || "")) ? (
                <VideoCard
                  src={activeItem?.url}
                  poster={activeItem?.poster_url}
                  fileId={activeItem?.file_id}
                  processing={activeItem?.processing}
                  autoplay
                  aspectClass="aspect-square md:aspect-[4/5]"
                  fitClass="object-contain md:object-cover"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setLightboxIndex(activeMedia)}
                  data-testid="post-media-open"
                  className="block w-full aspect-square md:aspect-[4/5] bg-black"
                >
                  <img src={fileUrl(activeItem?.url)} alt={project.title} className="w-full h-full object-contain md:object-cover" />
                </button>
              )}
            </motion.div>
          </AnimatePresence>

          {hasMany && (
            <>
              {activeMedia > 0 && (
                <button
                  onClick={() => setActiveMedia((i) => Math.max(0, i - 1))}
                  data-testid="post-prev"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {activeMedia < mediaList.length - 1 && (
                <button
                  onClick={() => setActiveMedia((i) => Math.min(mediaList.length - 1, i + 1))}
                  data-testid="post-next"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                {mediaList.map((_, i) => (
                  <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === activeMedia ? "bg-[color:var(--bda-blue-glow)] w-2.5" : "bg-white/55"}`} />
                ))}
              </div>
              <div className="absolute top-3 right-3 px-2 py-0.5 bg-black/60 text-white text-[10px] tracking-wider backdrop-blur">
                {activeMedia + 1}/{mediaList.length}
              </div>
            </>
          )}
        </div>

        {/* Audio player (admin-attached soundtrack) */}
        {project.audio_enabled !== false && project.audio_url && (
          <AudioPlayer
            url={project.audio_url}
            title={project.audio_title}
            autoplay={!!project.audio_autoplay}
          />
        )}

        {/* Action bar */}
        <div className="px-4 md:px-5 py-3" data-testid="post-actions">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {fLike && (
                <button
                  onClick={toggleLike}
                  data-testid="post-like-btn"
                  aria-label="Like"
                  className={`p-2 -ml-2 transition-colors ${engagement.liked ? "text-rose-500" : "text-[color:var(--bda-text)] hover:text-[color:var(--bda-text-muted)]"}`}
                >
                  <Heart className={`w-7 h-7 ${engagement.liked ? "fill-current" : ""}`} />
                </button>
              )}
              {fShare && (
                <button
                  onClick={nativeShare}
                  data-testid="post-share-btn"
                  aria-label="Share"
                  className="p-2 text-[color:var(--bda-text)] hover:text-[color:var(--bda-text-muted)]"
                >
                  <Send className="w-7 h-7" />
                </button>
              )}
              {fRepost && (
                <button
                  onClick={() => setShowRepost(true)}
                  data-testid="post-repost-btn"
                  aria-label="Repost"
                  className="p-2 text-[color:var(--bda-text)] hover:text-[color:var(--bda-text-muted)]"
                >
                  <Repeat2 className="w-7 h-7" />
                </button>
              )}
            </div>
          </div>

          {fLike && (
            <div className="text-sm font-semibold text-[color:var(--bda-text)] mt-1" data-testid="post-like-count">
              {(project.like_count || 0).toLocaleString()} {project.like_count === 1 ? "like" : "likes"}
            </div>
          )}

          <div className="text-sm text-[color:var(--bda-text)] mt-2 leading-relaxed" data-testid="post-caption">
            <span className="font-semibold mr-2">{settings?.brand_name?.toLowerCase().replace(/\s+/g, "") || "builddirectafrica"}</span>
            <span className="font-semibold">{project.title}.</span>{" "}
            <span className="whitespace-pre-line text-[color:var(--bda-text-muted)]">{project.description}</span>
          </div>

          {project.hashtags?.length > 0 && (
            <div className="text-xs text-[color:var(--bda-blue-glow)] mt-2 leading-relaxed">
              {project.hashtags.map((h) => `#${h.replace(/^#/, "")}`).join(" ")}
            </div>
          )}

          {fWhatsapp && (
            <a
              href={buildWhatsAppLink(
                settings?.whatsapp_number,
                `Hi BuildDirect Africa, I'm interested in this project — ${project.title} (${project.location || ''}).\n\n${typeof window !== 'undefined' ? window.location.href : ''}`,
              )}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onWhatsAppClick}
              data-testid="post-whatsapp-cta"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white px-5 py-3 text-sm font-semibold transition-colors"
            >
              <WhatsAppIcon className="w-4 h-4" color="#FFFFFF" />
              WhatsApp inquiry about this post
            </a>
          )}

          <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--bda-text-dim)] mt-3">
            {new Date(project.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </div>
        </div>
      </article>

      {showRepost && fRepost && <RepostMenu project={project} url={typeof window !== "undefined" ? window.location.href : ""} onClose={() => setShowRepost(false)} />}

      <Lightbox
        items={mediaList}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={() => setLightboxIndex((i) => (i - 1 + mediaList.length) % mediaList.length)}
        onNext={() => setLightboxIndex((i) => (i + 1) % mediaList.length)}
      />
    </div>
  );
}
